import {focusStatic} from '@pinecast/common/helpers/focusStatic';
import {useIntl} from '@pinecast/i18n';
import styled, {focusVisibleRing} from '@pinecast/styles';
import * as React from 'react';

/** The id of the page's `<main>`, which the skip link moves focus to. */
export const MAIN_CONTENT_ID = 'main-content';

const Main = styled('main', {
  // Focus lands here from the skip link. The landmark is not a control, so it
  // draws no ring of its own: the next Tab goes to the first control in it,
  // which does.
  ':focus': {
    // eslint-disable-next-line no-restricted-syntax -- not a control; see above
    outline: 'none',
  },
});

/**
 * The page's own content: not the header above it, and not the sidebar beside
 * it (WCAG 1.3.1). There is one on every dashboard page. `ChromeSidebar`
 * renders it next to its `<nav>`, and `Chrome` renders it for a page with no
 * sidebar.
 */
export const MainContent = ({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.ComponentProps<typeof Main>['style'];
}) => (
  // No `tabIndex` of its own: with one, a click on the text in it would move
  // focus to it, and the next Tab would start again at the top of the page.
  // The skip link adds one only while focus is on it.
  <Main id={MAIN_CONTENT_ID} style={style}>
    {children}
  </Main>
);

// Above the top of the page until it has focus, then over its top left
// corner. It jumps into place rather than sliding, so there is no motion to
// reduce.
const SkipAnchor = styled('a', (_: unknown, designColors) => ({
  backgroundColor: designColors.contentBackground,
  borderRadius: 4,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  color: designColors.contentText,
  fontSize: 14,
  left: 8,
  lineHeight: '20px',
  padding: '8px 16px',
  position: 'absolute',
  textDecoration: 'underline',
  top: 8,
  transform: 'translateY(-200%)',
  zIndex: 1000,
  ':focus': {
    transform: 'none',
  },
  ...focusVisibleRing(designColors),
}));

/**
 * The first thing the keyboard reaches on a dashboard page. It moves focus
 * past the header and the sidebar to the page's content (WCAG 2.4.1).
 */
export const SkipLink = () => {
  const intl = useIntl();
  return (
    <SkipAnchor
      href={`#${MAIN_CONTENT_ID}`}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        const main = document.getElementById(MAIN_CONTENT_ID);
        if (!main) {
          return;
        }
        // Focus it rather than follow the link: a hash in the address reaches
        // the pages that read it, such as the tabs of the Communication page.
        // It can take focus only until focus leaves it again.
        e.preventDefault();
        focusStatic(main);
      }}
    >
      {intl.formatMessage({
        description:
          'Link at the start of every dashboard page that moves focus past the header and the navigation to the page content, for keyboard and screen reader users',
        defaultMessage: 'Skip to main content',
      })}
    </SkipAnchor>
  );
};
